import { useCallback, useMemo, useState } from 'react';
import { SortChangeEventHandler, SortItem } from '@tanium/react-data-experience';

export type SortDirectionType = 'ASC' | 'DESC';

export interface UseColumnSortProps {
  sort?: SortItem[];
  onSortChange?: SortChangeEventHandler;
}

export interface ColumnSortProps {
  sort: SortItem[];
  toggleSort: (dataKey: string) => void;
  getColumnSortDirection: (dataKey: string) => SortDirectionType | undefined;
}

export interface ColumnSortHook {
  (props: UseColumnSortProps): ColumnSortProps;
}

const ASC = 'ASC';
const DESC = 'DESC';

/**
 * Stacked Ribbon List Hook to manage Column Sort
 */

export const useColumnSort: ColumnSortHook = ({ sort: sortFromProps, onSortChange }) => {
  const [sortState, setSort] = useState<SortItem[]>(sortFromProps || []);
  const sortProp = useMemo(() => sortFromProps, [sortFromProps]);

  const getSort = useCallback(() => sortProp || sortState, [sortProp, sortState]);

  const toggleSort = useCallback(
    (dataKey: string) => {
      const currentSort = getSort().find((i) => i.dataKey === dataKey);
      let newSort: SortItem[] = [];
      if (!currentSort) {
        newSort = [{ dataKey, sortDirection: ASC }];
      } else if (currentSort.sortDirection === ASC) {
        newSort = [{ dataKey, sortDirection: DESC }];
      }

      if (!sortFromProps) {
        setSort(newSort);
      }
      if (onSortChange) {
        onSortChange({ sort: newSort });
      }
    },
    [getSort, sortFromProps, onSortChange],
  );

  const getColumnSortDirection = useCallback(
    (dataKey: string) => {
      if (!getSort().length) {
        return undefined;
      }
      const sortItem = getSort().find((i) => i.dataKey === dataKey);
      return sortItem?.sortDirection;
    },
    [getSort],
  );

  return {
    getColumnSortDirection,
    toggleSort,
    sort: getSort(),
  };
};
